import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useEffect, useState } from 'react';
import Logo from "../../components/layout/utils/logo.svg";
import { Avatar, FormControl, Grid, InputLabel, Menu, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTranslation } from 'react-i18next';
import { menuItems } from './menu-items';
import React from 'react';
import { Language, getLanguages, handleSelectedLanguageChange } from '../../store/app-slice/app';
import i18next from 'i18next';
import { red } from '@mui/material/colors';

function LanguageSelection() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedLanguage, languages } = useAppSelector(
    (state: RootState) => state.app
  );

  function handleChange(e: SelectChangeEvent) {
    const language = languages.find(
      (el: Language) => el.id === parseInt(e?.target?.value)
    );
    if (language) {
      dispatch(handleSelectedLanguageChange(language));
      i18next.changeLanguage(language.code);
    }
  }

  return (
    <FormControl sx={{ m: 1, minWidth: 260 }} size="small">
      <InputLabel id="demo-select-small-label"></InputLabel>
      <Select
        labelId="demo-select-small-label"
        id="demo-select-small"
        value={selectedLanguage}
        label=""
        onChange={handleChange}
      >
        {languages.length === 0 ? (
          <MenuItem value="">
            <em>{t('translations:NO_AVAILABLE_LANGUAGES_TEXT')}</em>
          </MenuItem>
        ) : (
          languages.map((el: Language) => (
            <MenuItem key={el.id} value={el.id}>
              {el.name}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: 'white',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

export default function MainLayout(props : any) {
    const { user } = useAppSelector((state: RootState) => state.auth);  
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [width, setWidth] = useState<number>(window.innerWidth);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openProfile = Boolean(anchorEl);
    const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleProfileClose = () => {
      setAnchorEl(null);
    };
    const handleDrawerOpen = () => {
      setOpen(true);
    };
  
    const handleDrawerClose = () => {
      setOpen(false);
    };
    function handleWindowSizeChange() {
      setWidth(window.innerWidth);
    }
    
    useEffect(() => {
      window.addEventListener('resize', handleWindowSizeChange);
      return () => {
        window.removeEventListener('resize', handleWindowSizeChange);
      };
    }, []);
    const isMobile = width <= 768;

    var menus = menuItems(t, dispatch, navigate);
  
    return (
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open || isMobile ? { visibility: 'hidden' } : {}),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Grid container direction={{ xs: 'column', sm: 'row' }} mt={1} justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <a target="_blank" href='https://www.salute-futura.com/'>
                  <img style={{ width: '300px', display: 'block' }} src={Logo} alt="logo"/>
                </a>
                { process.env.REACT_APP_BUILD_ENV === "TEST" ? <Box component="span" sx={{ color: red[500], fontWeight: "bold", fontSize: 30, ml: 2 }}>TEST</Box> : "" }
              </Box>
              <Grid item display="flex" justifyContent="flex-end" mt={0.25} mb={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                <Tooltip title="Account">
                  <IconButton
                    onClick={handleProfileClick}
                    size="small"
                    sx={{ ml: 2 }}
                    aria-controls={openProfile ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openProfile ? 'true' : undefined}
                  >
                    <Avatar sx={{ width: 32, height: 32 }}>{user ? user.email?.charAt(0)?.toUpperCase() : 'A'}</Avatar>
                  </IconButton>
                </Tooltip>
              </Box>
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={openProfile}
                onClose={handleProfileClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem>
                  <AccountCircleIcon color="primary"></AccountCircleIcon>
                  <Typography ml={1} sx={{ typography: { sm: 'subtitle1', xs: 'body2' } }}>{user.email} ({user.role})</Typography>
                </MenuItem>
                <Divider/>
                <MenuItem>
                  <LanguageSelection />
                </MenuItem>
              </Menu>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        <Drawer variant="permanent" open={open}>
          <Grid container mt={{ xs: 5, sm: 0 }}>
            <DrawerHeader>
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </DrawerHeader>
          </Grid>
          <Divider />
          <List sx={{ paddingTop: 0 }}>
            {menus ? menus.map((el : any, index: number) => {
              if(el && (!el.roles || el.roles.length === 0 || el.roles?.includes(user.role))) {
                return(
                  <div key={index} className={ window.location.pathname === el?.route ? 'highlighted-sidebar' : '' }>
                    <ListItem disablePadding sx={{ display: 'block' }}>
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? 'initial' : 'center',
                          px: 2.5,
                        }}
                        onClick={() => {
                          el?.onClickFunction();
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          <Tooltip title={el?.title}>{el?.icon}</Tooltip>
                        </ListItemIcon>
                        <ListItemText primary={el?.title} sx={{ opacity: open ? 1 : 0 }} 
                          primaryTypographyProps={{
                            style: {
                              whiteSpace: 'pre-wrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}/>
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </div>)
              }
            }) : ''}
          </List>
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <DrawerHeader />
          <Grid mt={{ xs: 5, sm: 0 }}>
            {props.children}
          </Grid>
        </Box>
      </Box>
    );
}